import Select, { GroupBase } from 'react-select';
import { FC } from 'react';
import moment from 'moment';
import { uuid } from '../../../../_metronic/helpers';
import { Client, INoteTemplate, Provider } from '../../../helpers/models';
import { WIZ_THEME } from './WizSelect';
import { useNoteTemplates, useNotes } from '../../../helpers/api';
import { Note } from './_models';

type OptionType = { value: string; label: string };

type Props = {
  client?: Client;
  provider?: Provider;
  excludeNoteUuid?: uuid;
  noteTemplate?: INoteTemplate | undefined;
  onChange?: (newNoteTemplate?: INoteTemplate) => void;
  onSelectNote?: (note: Note) => void;
  className?: string;
  classNamePrefix?: string;
  disabled?: boolean;
  placeHolder?: string;
};

const priorNoteLabel = (n: Note): string => {
  if (n.title) return n.title;
  const evl: any = (n as any).event_log;
  if (evl?.start) {
    const date = moment(evl.start).format('LL');
    return evl.provider_service_name ? `${date} · ${evl.provider_service_name}` : date;
  }
  if (n.created_at) return moment(n.created_at).format('LL');
  return '(untitled)';
};

const NoteTemplateSelect: FC<Props> = (props) => {
  const { status, data: noteTemplates, isLoading, isFetching } = useNoteTemplates();
  const { data: notesData } = useNotes(props.client, '', !!props.client);

  const templateOptions: OptionType[] =
    status === 'success'
      ? noteTemplates.map((nt: INoteTemplate) => ({ value: `tpl:${nt.uuid}`, label: nt.title || '' }))
      : [];

  const priorNotes: Note[] = (notesData || [])
    .filter((n) => n.uuid !== props.excludeNoteUuid)
    .filter((n) => !props.provider || (n.provider as any)?.uuid === props.provider.uuid)
    .slice(0, 5);

  const priorNoteOptions: OptionType[] = priorNotes.map((n) => ({
    value: `note:${n.uuid}`,
    label: priorNoteLabel(n),
  }));

  const groupedOptions: GroupBase<OptionType>[] = [];
  if (templateOptions.length) groupedOptions.push({ label: 'Templates', options: templateOptions });
  if (priorNoteOptions.length) groupedOptions.push({ label: 'Prior Notes', options: priorNoteOptions });

  return (
    <Select
      theme={WIZ_THEME}
      classNamePrefix={props.classNamePrefix}
      className={props.className}
      aria-label="Template or Prior Note Selection"
      placeholder={props.placeHolder || 'Select Template or Prior Note...'}
      value={
        props.noteTemplate
          ? { value: `tpl:${props.noteTemplate.uuid}`, label: props.noteTemplate.title || '' }
          : null
      }
      onChange={(val: any) => {
        if (!val) return;
        if (typeof val.value === 'string' && val.value.startsWith('tpl:')) {
          const uuid = val.value.slice(4);
          const tpl = (noteTemplates || []).find((nt: INoteTemplate) => uuid === nt.uuid);
          if (props.onChange) props.onChange(tpl);
        } else if (typeof val.value === 'string' && val.value.startsWith('note:')) {
          const uuid = val.value.slice(5);
          const n = priorNotes.find((pn) => pn.uuid === uuid);
          if (n && props.onSelectNote) props.onSelectNote(n);
        }
      }}
      options={groupedOptions}
      isSearchable={false}
      styles={{
        menu: (base) => ({ ...base, zIndex: 1000 }),
        placeholder: (base) => ({ ...base, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }),
      }}
      isLoading={isLoading || isFetching}
      noOptionsMessage={() => 'No templates or prior notes available'}
    />
  );
};

export { NoteTemplateSelect };
